import type {IndexCopilotSpace} from '@github-ui/copilot-chat-types'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ActionList, SelectPanel, type SelectPanelItemInput as ItemInput, Button} from '@primer/react'
import {useState, useRef, useEffect, useCallback, useMemo} from 'react'

import {useCustomCopilotsEnabled} from '../hooks'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import {COPILOT_PATH} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'

import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {SpaceIcon, StarFillIcon, ChevronRightIcon} from '@primer/octicons-react'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'

const SEARCH_DEBOUNCE_MS = 350
const MAX_SPACE_NAME_LENGTH = 40

const StarredIcon = () => (
  <span role="img" aria-label="Starred" style={{color: 'var(--button-star-iconColor)'}}>
    <StarFillIcon />
  </span>
)

const getSpaceKey = (space: {owner: string; id: number}) => `${space.owner}-${space.id}`

export function SpacesSelectNew({
  openCreateSpaceDialog,
  setPendingSpace,
  clearSpaceSelection,
  activeCopilotSpaceId,
  readOnly,
}: {
  openCreateSpaceDialog: (show: boolean) => void
  setPendingSpace: (space: {id: number; owner: string}) => void
  clearSpaceSelection: () => void
  activeCopilotSpaceId: {id: number; owner: string} | null
  readOnly: boolean
}) {
  const manager = useChatManager()
  const [open, setOpen] = useState(false)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [filter, setFilter] = useState('')
  const [debouncedFilter, setDebouncedFilter] = useState('')
  const [filteredSpaces, setFilteredSpaces] = useState<IndexCopilotSpace[]>([])
  const [loading, setLoading] = useState(false)
  const [showCreateSpace, setShowCreateSpace] = useState(false)

  useEffect(() => {
    // We only want to show the "Create a space" button in /copilot
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShowCreateSpace(window.location.pathname.startsWith(COPILOT_PATH))
  }, [])

  const handleMenuOpen = (value: boolean) => {
    if (value) {
      sendEvent('dotcom_chat.activate', {target: 'ATTACHMENT_MENU_COPILOT_SPACES', mode: 'immersive'})
    }
    setOpen(value)
  }

  const handleCreateSpace = () => {
    setOpen(false)
    openCreateSpaceDialog(true)
    sendEvent('dotcom_chat.activate', {
      target: 'ATTACHMENT_MENU_COPILOT_SPACES_CREATE_SPACE',
      mode: 'immersive',
    })
  }

  const handleSpaceSelect = useCallback(
    (space: IndexCopilotSpace) => {
      const isCurrentlySelected =
        activeCopilotSpaceId && space.id === activeCopilotSpaceId.id && space.owner === activeCopilotSpaceId.owner

      if (isCurrentlySelected) {
        clearSpaceSelection()
      } else {
        manager.addReference({...space, type: 'copilot-space'}, 'spaces')
        setPendingSpace({id: space.id, owner: space.owner})
        sendEvent('dotcom_chat.activate', {
          target: 'ATTACHMENT_MENU_COPILOT_SPACES_SELECT_SPACE',
          space: `${space.owner}/${space.id}`,
        })
      }
      setOpen(false)
    },
    [activeCopilotSpaceId, clearSpaceSelection, manager, setPendingSpace],
  )

  const fetchResults = useCallback(async (searchQuery: string) => {
    try {
      const params = new URLSearchParams({
        q: searchQuery,
      })

      const res = await verifiedFetchJSON(`/github-copilot/chat/custom_copilots/search?${params.toString()}`)

      if (!res.ok) throw new Error('Failed to fetch spaces')
      const data = (await res.json()) as IndexCopilotSpace[]
      return data
    } catch {
      return []
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const fetchAndSetSpaces = async () => {
      if (!open) return
      setLoading(true)

      const spaces = await fetchResults(debouncedFilter)
      if (cancelled) return

      setFilteredSpaces(spaces)
      setLoading(false)
    }
    fetchAndSetSpaces()
    return () => {
      cancelled = true
    }
  }, [fetchResults, debouncedFilter, open])

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [])

  const onSelectedChange = (selectedItem: ItemInput | undefined) => {
    const selectedSpace = filteredSpaces.find(space => getSpaceKey(space) === selectedItem?.id)
    if (selectedSpace) {
      handleSpaceSelect(selectedSpace)
      copilotLocalStorage.setLastSelectedSpace(selectedSpace)
    }
  }

  const copilotSpacesEnabled = useCustomCopilotsEnabled()

  const items: ItemInput[] = useMemo(
    () =>
      filteredSpaces.map(space => ({
        id: getSpaceKey(space),
        key: getSpaceKey(space),
        text:
          space.name.length > MAX_SPACE_NAME_LENGTH ? `${space.name.slice(0, MAX_SPACE_NAME_LENGTH)}...` : space.name,
        description: space.owner,
        descriptionVariant: 'inline',
        value: getSpaceKey(space),
        trailingVisual: space.starred ? StarredIcon : undefined,
      })),
    [filteredSpaces],
  )

  // If readOnly (if a thread exists) hide the menu. We can't yet assign
  // a space to an existing thread.
  if (!copilotSpacesEnabled || readOnly) {
    return null
  }

  const selected = activeCopilotSpaceId ? items.find(item => item.id === getSpaceKey(activeCopilotSpaceId)) : undefined

  const singleSelectProps = {
    selected,
    onSelectedChange,
  }

  return (
    <>
      <SelectPanel
        align="end"
        title="Select a space"
        subtitle="Recent spaces"
        renderAnchor={props => (
          <ActionList.Item {...(props as React.ComponentProps<typeof ActionList.Item>)}>
            <ActionList.LeadingVisual>
              <SpaceIcon />
            </ActionList.LeadingVisual>
            Spaces&hellip;
            <ActionList.TrailingVisual>
              <ChevronRightIcon />
            </ActionList.TrailingVisual>
          </ActionList.Item>
        )}
        open={open}
        onOpenChange={handleMenuOpen}
        loading={loading}
        items={items}
        filterValue={filter}
        onFilterChange={(value: string) => {
          setFilter(value)
          if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
          debounceTimeoutRef.current = setTimeout(() => {
            setDebouncedFilter(value)
          }, SEARCH_DEBOUNCE_MS)
        }}
        {...singleSelectProps}
        overlayProps={{
          height: 'auto',
          width: 'large',
          anchorSide: 'outside-right',
        }}
        message={
          filteredSpaces.length === 0 && !loading
            ? {
                title: 'No spaces found',
                body: 'You can create a new space to get started.',
                variant: 'empty',
                action: (
                  <Button variant="default" size="small" onClick={handleCreateSpace}>
                    Create a new space
                  </Button>
                ),
              }
            : undefined
        }
        secondaryAction={
          showCreateSpace && filteredSpaces.length > 0 ? (
            <SelectPanel.SecondaryActionButton onClick={handleCreateSpace}>
              Create a space
            </SelectPanel.SecondaryActionButton>
          ) : undefined
        }
      />
    </>
  )
}
